import type { Region } from '@/shared/utils/regions';
import type { Iso8601, Uuid } from '@/shared/types/api/common';
import type { ApiParcelsProductsGetResponse } from '@/shared/types/api/parcels-products';
import type { SplitStatus } from '@/shared/types/api/splits';

export const PARCEL_STATUS_VALUES = ['DRAFT', 'PENDING', 'APPROVED', 'REJECTED'] as const;
export type ParcelStatus = (typeof PARCEL_STATUS_VALUES)[number];

export const PARTY_STATUS_VALUES = [
  'COLLECTING',
  'SHIPPING',
  'ON_THE_WAY',
  'ON_CUSTOMS',
  'LOCAL_ON_THE_WAY',
  'SHIPMENT_ON_CUSTOMERS',
] as const;
export type PartyStatus = (typeof PARTY_STATUS_VALUES)[number];

// Client-facing canonical workflow status, persisted on the model now.
// Lifecycle:
//  - 'IN_PROGRESS' fresh parcel (user hasn't pressed Confirm shipping yet)
//  - 'SORTING'     user confirmed; awaiting attach to a Party
//  - any PartyStatus code: parcel mirrors its Party's status while attached
export const CLIENT_STATUS_VALUES = [
  'IN_PROGRESS',
  'SORTING',
  ...PARTY_STATUS_VALUES,
] as const;
export type ClientStatus = (typeof CLIENT_STATUS_VALUES)[number];

// Legacy alias: the backend's ``fulfillment_status`` field now carries the
// same workflow enum as ``client_status``. Kept as a re-export so older
// imports don't break.
export type FulfillmentStatus = ClientStatus;

export type ApiParcelPartyInfo = {
  id: Uuid;
  name: string;
  status: PartyStatus;
};

export type ApiParcelsGetItem = {
  id: Uuid;
  name: string;
  trek_id: string;
  store_name?: string | null;
  address_name: string;
  first_name: string;
  last_name: string;
  middle_name: string;
  street: string;
  building: string;
  apartment?: string | null;
  region: Region;
  city: string;
  phone_number: string;
  passport_series: string;
  pinfl: string;
  additional_info?: string | null;
  status: ParcelStatus;
  fulfillment_status: FulfillmentStatus;
  // Canonical client-facing status returned by the API for user endpoints.
  // Falls back to undefined for endpoints / older builds that don't emit it.
  client_status?: ClientStatus;
  // Owner spec (2026-05-31): True while the physical box is inside the JP
  // warehouse (workflow in IN_PROGRESS/SORTING/COLLECTING). Confirm-shipping
  // is gated on this flag — a parcel that hasn't physically arrived cannot
  // be confirmed. Optional so older payloads don't break older builds.
  is_in_warehouse?: boolean;
  // Split tracking (2026-06): True when this parcel has been split into
  // multiple recipient targets by warehouse staff. ``split_status`` mirrors
  // the split's lifecycle, and ``delivered_to_customer`` flips once the
  // physical handover to the end customer is complete. All optional so older
  // payloads / non-split parcels don't break.
  is_split?: boolean;
  split_status?: SplitStatus | null;
  delivered_to_customer?: boolean;
  reject_reason?: string | null;
  user_id?: Uuid | null;
  party_id?: Uuid | null;
  party?: ApiParcelPartyInfo | null;
  is_deleted_by_user: boolean;
  created_by_user: boolean;
  total_amount: number;
  products?: ApiParcelsProductsGetResponse;
  created_at: Iso8601;
  updated_at: Iso8601;
};

export type ApiParcelsMyGetResponse = ApiParcelsGetItem[];

export type ApiParcelsDetailGetResponse = ApiParcelsGetItem;

export type ApiParcelsPostBody = {
  name: string;
  trek_id: string;
  store_name?: string;
  address_name: string;
  first_name: string;
  last_name: string;
  middle_name: string;
  street: string;
  building: string;
  apartment?: string;
  region: Region;
  city: string;
  phone_number: string;
  passport_series: string;
  pinfl: string;
  additional_info?: string;
};

export type ApiParcelsPutBody = Partial<ApiParcelsPostBody>;

export type ApiParcelsClaimPostBody = {
  pinfl: string;
  trek_id: string;
};

export type ApiParcelsConfirmShippingPostBody = {
  parcel_ids: Uuid[];
};

export type ApiParcelsConfirmShippingPostResponse = {
  confirmed: number;
  confirmed_ids: Uuid[];
  skipped: { id: Uuid; reason: string }[];
};

// GET /parcels/my/debts — arrived parcels with unpaid debt (amounts are strings).
export type ApiParcelsMyDebtsItem = {
  id: Uuid;
  name: string;
  trek_id: string;
  debt_amount: string;
};

export type ApiParcelsMyDebts = {
  total_debt: string;
  parcels: ApiParcelsMyDebtsItem[];
};
